import { useMutation, useQueryClient } from "@tanstack/react-query";

import { showHiddenAuctionsAtom } from "@/src/state/dealer/showHiddenAuctionsAtom";

import { DealerAuctionDTO } from "@/src/types/types";
import { hasHiddenAuctions } from "@/src/utils/auctionUtils";

import { DealerQueryKeys, toggleHide } from "./dealerFunctions";

import { useSetAtom } from "jotai";

/**
 * Updates auction state optimistically by toggling isHidden property.
 * @param currentArray
 * @param auctionId
 */
const updateAuctionsState = (
  currentArray: DealerAuctionDTO[],
  auctionId: number
) => {
  const updatedArray = [...currentArray];
  const oldAuctionIndex = updatedArray.findIndex(({ id }) => id === auctionId);
  if (oldAuctionIndex !== -1) {
    const oldAuction = updatedArray[oldAuctionIndex];
    updatedArray[oldAuctionIndex] = {
      ...oldAuction,
      isHidden: !oldAuction.isHidden
    };
  }
  return updatedArray;
};

export const usePostHide = () => {
  const queryClient = useQueryClient();
  const setShowHiddenAuctions = useSetAtom(showHiddenAuctionsAtom);

  return useMutation({
    mutationFn: toggleHide,
    onMutate: async ({ auctionId }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: [DealerQueryKeys.AUCTION] });
      await queryClient.cancelQueries({ queryKey: [DealerQueryKeys.AUCTIONS] });
      await queryClient.cancelQueries({
        queryKey: [DealerQueryKeys.MY_ACTIVE_AUCTIONS]
      });
      await queryClient.cancelQueries({
        queryKey: [DealerQueryKeys.AUCTIONS_SUMMARY]
      });

      // Snapshot the previous values for rollback
      const previousAuction = queryClient.getQueryData([
        DealerQueryKeys.AUCTION,
        auctionId
      ]) as DealerAuctionDTO | undefined;

      const previousAuctions = queryClient.getQueryData([
        DealerQueryKeys.AUCTIONS
      ]) as DealerAuctionDTO[] | undefined;

      const previousMyAuctions = queryClient.getQueryData([
        DealerQueryKeys.MY_ACTIVE_AUCTIONS
      ]) as DealerAuctionDTO[] | undefined;

      // Optimistically update single auction query
      if (previousAuction?.id === auctionId) {
        queryClient.setQueryData([DealerQueryKeys.AUCTION, auctionId], {
          ...previousAuction,
          isHidden: !previousAuction.isHidden
        });
      }

      // Optimistically update auctions array queries
      if (previousAuctions) {
        const newAuctions = updateAuctionsState(previousAuctions, auctionId);
        queryClient.setQueryData([DealerQueryKeys.AUCTIONS], newAuctions);
      }

      if (previousMyAuctions) {
        const newMyAuctions = updateAuctionsState(
          previousMyAuctions,
          auctionId
        );
        queryClient.setQueryData(
          [DealerQueryKeys.MY_ACTIVE_AUCTIONS],
          newMyAuctions
        );
      }

      return {
        previousAuction,
        previousAuctions,
        previousMyAuctions
      };
    },
    onError: (error, variables, context) => {
      // Rollback optimistic updates on error
      if (context?.previousAuction) {
        queryClient.setQueryData(
          [DealerQueryKeys.AUCTION, variables.auctionId],
          context.previousAuction
        );
      }
      if (context?.previousAuctions) {
        queryClient.setQueryData(
          [DealerQueryKeys.AUCTIONS],
          context.previousAuctions
        );
      }
      if (context?.previousMyAuctions) {
        queryClient.setQueryData(
          [DealerQueryKeys.MY_ACTIVE_AUCTIONS],
          context.previousMyAuctions
        );
      }
    },
    onSuccess: () => {
      const auctions = queryClient.getQueryData([DealerQueryKeys.AUCTIONS]) as
        | DealerAuctionDTO[]
        | undefined;
      const myAuctions = queryClient.getQueryData([
        DealerQueryKeys.MY_ACTIVE_AUCTIONS
      ]) as DealerAuctionDTO[] | undefined;

      // A cache that hasn't been fetched this session is unknown, not empty —
      // only reset the shared showHiddenAuctions toggle once both caches are
      // loaded and confirmed to have no hidden auctions.
      if (!auctions || !myAuctions) {
        return;
      }

      if (!hasHiddenAuctions(auctions) && !hasHiddenAuctions(myAuctions)) {
        setShowHiddenAuctions(false);
      }
    },
    // Triggered on both onSuccess & onError
    onSettled: () => {
      // Invalidate queries to ensure we have the latest data from server
      queryClient.invalidateQueries({ queryKey: [DealerQueryKeys.AUCTION] });
      queryClient.invalidateQueries({ queryKey: [DealerQueryKeys.AUCTIONS] });
      queryClient.invalidateQueries({
        queryKey: [DealerQueryKeys.AUCTIONS_SUMMARY]
      });
      queryClient.invalidateQueries({
        queryKey: [DealerQueryKeys.MY_ACTIVE_AUCTIONS]
      });
    }
  });
};
